import { useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { WebhookStatusBadge } from "@/components/ezyvet/WebhookStatusBadge";
import type { WebhookLogEntry } from "@/types/ezyvet";

/** Raw payload and error trace of one inbound webhook. */
export function PayloadInspector({ entry, onClose }: { entry: WebhookLogEntry | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const json = useMemo(() => (entry ? JSON.stringify(entry.payload, null, 2) : ""), [entry]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard refused; the text is selectable */
    }
  };

  return (
    <Modal
      open={entry !== null}
      onClose={onClose}
      title={entry ? `Webhook ${entry.event_type ?? "event"}` : ""}
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn-sm" onClick={copy}>
            {copied ? "Copied" : "Copy JSON"}
          </button>
          <button type="button" className="btn btn-sm btn-primary" onClick={onClose} data-autofocus>
            Close
          </button>
        </>
      }
    >
      {entry && (
        <>
          <dl className="kv kv--compact">
            <dt>Status</dt>
            <dd>
              <WebhookStatusBadge status={entry.status} />
            </dd>
            <dt>Event id</dt>
            <dd className="mono">{entry.event_id}</dd>
            <dt>Received</dt>
            <dd>{new Date(entry.received_at).toLocaleString()}</dd>
            <dt>Processed</dt>
            <dd>{entry.processed_at ? new Date(entry.processed_at).toLocaleString() : "—"}</dd>
          </dl>
          {entry.error && (
            <>
              <h3>Error trace</h3>
              <pre className="code-block code-block--error">{entry.error}</pre>
            </>
          )}
          <h3>Payload</h3>
          <pre className="code-block" tabIndex={0} aria-label="Raw webhook payload">
            {json}
          </pre>
        </>
      )}
    </Modal>
  );
}
